import { HCO_HIERARCHY_CUBE_NAME, HCP_IN_HCO_CUBE_NAME } from '@/api/hco/constants';
import {
  buildDepartmentCountQueryFilters,
  buildDeptFilters,
  useDepartmentCountGlobalFilterState,
} from '@/api/hco/departmentCountFilters';
import { useHcoAppliedFilters } from '@/api/hco/useHcoAppliedFilters';
import { HCOKeys } from '@/api/queries/hco/cacheKeys';
import useQuery from '@/api/queries/useQuery';
import { CubeQuery } from '@/types/cube';
import { CubeRawRow } from '@/utils/cube/mapper.type';
import { Filter } from '@cubejs-client/core';
import { UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';

export interface StructureSummaryCounts {
  hcpCount: number;
  departmentCount: number;
}

function buildHcpHcoOnlyFilters(hcoBk: string): Filter[] {
  return [
    { member: `${HCP_IN_HCO_CUBE_NAME}.child_hco_bk`, operator: 'equals', values: [hcoBk] },
    { member: `${HCP_IN_HCO_CUBE_NAME}.hco_path`, operator: 'contains', values: [hcoBk] },
  ];
}

function buildHcpAllDepartmentsFilters(hcoBk: string): Filter[] {
  return [
    {
      or: [
        { member: `${HCO_HIERARCHY_CUBE_NAME}.hco_bk`, operator: 'equals', values: [hcoBk] },
        { member: `${HCO_HIERARCHY_CUBE_NAME}.path_mod`, operator: 'contains', values: [hcoBk] },
      ],
    },
  ];
}

function buildHcpFilters(hcoBk: string | undefined, includeAllDepartments: boolean): Filter[] {
  if (!hcoBk) return [];
  if (includeAllDepartments) return buildHcpAllDepartmentsFilters(hcoBk);
  return buildHcpHcoOnlyFilters(hcoBk);
}

export function selectCount(data: CubeRawRow[] | null | undefined, measureKey: string): number {
  if (!data || data.length === 0) return 0;
  const parsed = Number(data[0][measureKey]);
  return Number.isNaN(parsed) ? 0 : parsed;
}

interface StructureSummaryHcpCountResult {
  hcp: UseQueryResult<number, AxiosError>;
  isLoading: boolean;
  isError: boolean;
}

interface StructureSummaryDepartmentCountResult {
  department: UseQueryResult<number, AxiosError>;
  isLoading: boolean;
  isError: boolean;
}

interface StructureSummaryQueryResults {
  hcp: UseQueryResult<number, AxiosError>;
  department: UseQueryResult<number, AxiosError>;
  isHcpLoading: boolean;
  isDepartmentLoading: boolean;
  isHcpError: boolean;
  isDepartmentError: boolean;
}

const useGetStructureSummaryHcpCount = (
  hcoBk: string | undefined,
  includeAllDepartments: boolean,
): StructureSummaryHcpCountResult => {
  const { appliedFilters, isResolving, isResolverFailed } = useHcoAppliedFilters(hcoBk, includeAllDepartments);
  const hcpFilters = buildHcpFilters(hcoBk, includeAllDepartments);
  const isResolverLoading = isResolving && !isResolverFailed;

  const hcpQuery: CubeQuery = {
    measures: [`${HCP_IN_HCO_CUBE_NAME}.distinctHCPcount`],
    filters: [appliedFilters, ...hcpFilters],
    limit: 1,
  };

  const hcp = useQuery<CubeRawRow[], AxiosError, number>({
    payload: hcpQuery,
    options: {
      queryKey: HCOKeys.hcpCount(hcoBk ?? '', includeAllDepartments, hcpQuery),
      select: data => selectCount(data, `${HCP_IN_HCO_CUBE_NAME}.distinctHCPcount`),
      enabled: !!hcoBk && !isResolving,
    },
  });

  return {
    hcp,
    isLoading: isResolverLoading || hcp.isLoading,
    isError: isResolverFailed || hcp.isError,
  };
};

const useGetStructureSummaryDepartmentCount = (
  hcoBk: string | undefined,
  includeAllDepartments: boolean,
): StructureSummaryDepartmentCountResult => {
  const deptFilters = buildDeptFilters(hcoBk, includeAllDepartments);
  const { deptGlobalFilters, hasActiveDeptGlobalFilters, hasActiveHcpGlobalFilters } =
    useDepartmentCountGlobalFilterState(includeAllDepartments);
  const deptQueryFilters = buildDepartmentCountQueryFilters(
    deptFilters,
    deptGlobalFilters,
    hasActiveDeptGlobalFilters,
    hasActiveHcpGlobalFilters,
    includeAllDepartments ? 'all-departments' : 'hco-only',
    hcoBk ?? '',
  );

  const deptQuery: CubeQuery = {
    measures: [`${HCO_HIERARCHY_CUBE_NAME}.distinctDeptCount`],
    filters: deptQueryFilters,
    limit: 1,
  };

  const department = useQuery<CubeRawRow[], AxiosError, number>({
    payload: deptQuery,
    options: {
      queryKey: HCOKeys.departmentCount(hcoBk ?? '', includeAllDepartments, deptQuery),
      select: data => selectCount(data, `${HCO_HIERARCHY_CUBE_NAME}.distinctDeptCount`),
      enabled: !!hcoBk,
    },
  });

  return {
    department,
    isLoading: department.isLoading,
    isError: department.isError,
  };
};

const useGetStructureSummaryCounts = (
  hcoBk: string | undefined,
  includeAllDepartments: boolean,
): StructureSummaryQueryResults => {
  const {
    hcp,
    isLoading: isHcpLoading,
    isError: isHcpError,
  } = useGetStructureSummaryHcpCount(hcoBk, includeAllDepartments);
  const {
    department,
    isLoading: isDepartmentLoading,
    isError: isDepartmentError,
  } = useGetStructureSummaryDepartmentCount(hcoBk, includeAllDepartments);

  return {
    hcp,
    department,
    isHcpLoading,
    isDepartmentLoading,
    isHcpError,
    isDepartmentError,
  };
};

export { useGetStructureSummaryCounts, useGetStructureSummaryDepartmentCount, useGetStructureSummaryHcpCount };
